export const clearScrollAnimInlineStyles = (el) => {
	if (!el || !el.style) return
	if (el.style.transform) el.style.transform = ''
	if (el.style.opacity) el.style.opacity = ''
	if (el.style.filter) el.style.filter = ''
	if (el.style.clipPath) el.style.clipPath = ''
	if (el.style.willChange) el.style.willChange = ''
}

const easings = {
	linear: (t) => t,
	easeInQuad: (t) => t * t,
	easeOutQuad: (t) => t * (2 - t),
	easeInOutQuad: (t) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t),
	easeInCubic: (t) => t * t * t,
	easeOutCubic: (t) => --t * t * t + 1,
	easeInOutCubic: (t) => (t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1),
	easeInQuart: (t) => t * t * t * t,
	easeOutQuart: (t) => 1 - --t * t * t * t,
	easeInOutQuart: (t) => (t < 0.5 ? 8 * t * t * t * t : 1 - 8 * --t * t * t * t),
	easeOutQuint: (t) => 1 + --t * t * t * t * t,
	easeInSine: (t) => 1 - Math.cos((t * Math.PI) / 2),
	easeOutSine: (t) => Math.sin((t * Math.PI) / 2),
	easeInOutSine: (t) => -(Math.cos(Math.PI * t) - 1) / 2,
	easeInExpo: (t) => (t === 0 ? 0 : Math.pow(2, 10 * (t - 1))),
	easeOutExpo: (t) => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t)),
	easeInOutExpo: (t) => {
		if (t === 0) return 0
		if (t === 1) return 1
		if (t < 0.5) return Math.pow(2, 20 * t - 10) / 2
		return (2 - Math.pow(2, -20 * t + 10)) / 2
	},
	easeOutCirc: (t) => Math.sqrt(1 - --t * t),
	easeInOutCirc: (t) => {
		if (t < 0.5) return (1 - Math.sqrt(1 - 4 * t * t)) / 2
		return (Math.sqrt(1 - (-2 * t + 2) * (-2 * t + 2)) + 1) / 2
	},
	easeOutBack: (t) => {
		const c1 = 1.70158
		const c3 = c1 + 1
		return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2)
	},
	easeInOutBack: (t) => {
		const c1 = 1.70158
		const c2 = c1 * 1.525
		if (t < 0.5) return (Math.pow(2 * t, 2) * ((c2 + 1) * 2 * t - c2)) / 2
		return (Math.pow(2 * t - 2, 2) * ((c2 + 1) * (t * 2 - 2) + c2) + 2) / 2
	},
}

export const easingFor = (name) => easings[name] || easings.easeOutCubic

const DIRECTIONS = {
	left: { fromRight: false, fromLeft: true, fromBottom: false, fromTop: false },
	right: { fromRight: true, fromLeft: false, fromBottom: false, fromTop: false },
	up: { fromRight: false, fromLeft: false, fromBottom: true, fromTop: false, verticalOnly: true },
	down: { fromRight: false, fromLeft: false, fromBottom: false, fromTop: true, verticalOnly: true },
	bottomLeft: { fromRight: false, fromLeft: true, fromBottom: true, fromTop: false },
	bottomRight: { fromRight: true, fromLeft: false, fromBottom: true, fromTop: false },
}

const applyOpacity = (el, easedProgress, fadeEnabled, fadeFrom, cache) => {
	let next
	if (!fadeEnabled || fadeFrom >= 1) {
		next = cache.baseOpacity || ''
	} else {
		const baseOpacity = cache.baseOpacity !== '' ? parseFloat(cache.baseOpacity) : 1
		const opacity = fadeFrom + (1 - fadeFrom) * easedProgress
		next = (baseOpacity * opacity).toFixed(3)
	}
	if (cache._lastOpacity === next) return
	cache._lastOpacity = next
	el.style.opacity = next
}

const applyFilter = (el, easedProgress, blurEnabled, blurFromPx, grayscaleEnabled, grayscaleFromPct, brightnessEnabled, brightnessFromPct, cache) => {
	let next = ''
	if (blurEnabled || grayscaleEnabled || brightnessEnabled) {
		const inv = 1 - easedProgress
		if (blurEnabled && blurFromPx > 0) {
			const v = blurFromPx * inv
			if (v > 0.05) next += 'blur(' + v.toFixed(2) + 'px) '
		}
		if (grayscaleEnabled && grayscaleFromPct > 0) {
			const v = grayscaleFromPct * inv
			if (v > 0.5) next += 'grayscale(' + v.toFixed(2) + '%) '
		}
		if (brightnessEnabled) {
			const from = brightnessFromPct / 100
			const v = from + (1 - from) * easedProgress
			if (Math.abs(v - 1) > 0.005) next += 'brightness(' + v.toFixed(3) + ') '
		}
		if (next.length > 0) next = next.substring(0, next.length - 1)
	}
	if (cache._lastFilter === next) return
	cache._lastFilter = next
	el.style.filter = next
}

const applyClipPath = (el, easedProgress, revealEnabled, revealDirection, cache) => {
	let clip
	if (!revealEnabled) {
		clip = ''
	} else {
		const visible = (easedProgress * 100).toFixed(2)
		const start = (100 - parseFloat(visible)).toFixed(2)
		switch (revealDirection) {
			case 'right':
				clip = 'polygon(' + start + '% 0, 100% 0, 100% 100%, ' + start + '% 100%)'
				break
			case 'top':
				clip = 'polygon(0 ' + start + '%, 100% ' + start + '%, 100% 100%, 0 100%)'
				break
			case 'bottom':
				clip = 'polygon(0 0, 100% 0, 100% ' + visible + '%, 0 ' + visible + '%)'
				break
			case 'circle': {
				const r = (easedProgress * 50).toFixed(2)
				clip = 'ellipse(' + r + '% ' + r + '% at 50% 50%)'
				break
			}
			case 'left':
			default:
				clip = 'polygon(0 0, ' + visible + '% 0, ' + visible + '% 100%, 0 100%)'
				break
		}
	}
	if (cache._lastClipPath === clip) return
	cache._lastClipPath = clip
	el.style.clipPath = clip
}

export const isUsableNaturalRect = (rect) => {
	return !!rect && (rect.width > 0 || rect.height > 0)
}

export const rememberNaturalRect = (cfg, rect) => {
	if (!cfg || !isUsableNaturalRect(rect)) return
	cfg._lastNaturalRect = {
		top: rect.top,
		left: rect.left,
		right: rect.right,
		bottom: rect.bottom,
		width: rect.width,
		height: rect.height,
	}
}

const buildTransform = (naturalRect, easedProgress, scaleEnabled, scaleFactor, directionEnabled, direction, distancePct, skewEnabled, skewFromX, skewFromY, rotateEnabled, rotateFromX, rotateFromY, rotateFromZ, scaleToElementSize) => {
	let out = ''

	if (directionEnabled) {
		const d = DIRECTIONS[direction] || DIRECTIONS.left
		if (d.verticalOnly) {
			const ySign = d.fromBottom ? 1 : -1
			const ty = ySign * distancePct * 100 * (1 - easedProgress)
			out += 'translate(0px,' + ty.toFixed(2) + '%) '
		} else if (d.fromBottom) {
			const xSign = d.fromRight ? 1 : -1
			const tx = xSign * distancePct * 100 * (1 - easedProgress)
			const ty = distancePct * 100 * (1 - easedProgress)
			out += 'translate(' + tx.toFixed(2) + '%,' + ty.toFixed(2) + '%) '
		} else {
			let fullOffsetX = 0
			if (d.fromRight) {
				fullOffsetX = (window.innerWidth - naturalRect.left) * distancePct
			} else {
				fullOffsetX = -naturalRect.right * distancePct
			}
			if (scaleToElementSize && naturalRect.width > 0) {
				const cap = naturalRect.width
				if (fullOffsetX > cap) fullOffsetX = cap
				else if (fullOffsetX < -cap) fullOffsetX = -cap
			}
			const tx = fullOffsetX * (1 - easedProgress)
			out += 'translate(' + (tx | 0) + 'px,0px) '
		}
	}

	if (scaleEnabled) {
		const scale = scaleFactor + (1 - scaleFactor) * easedProgress
		out += 'scale(' + scale.toFixed(4) + ') '
	}

	if (skewEnabled) {
		const sx = skewFromX * (1 - easedProgress)
		const sy = skewFromY * (1 - easedProgress)
		out += 'skew(' + sx.toFixed(2) + 'deg,' + sy.toFixed(2) + 'deg) '
	}

	if (rotateEnabled) {
		const rx = rotateFromX * (1 - easedProgress)
		const ry = rotateFromY * (1 - easedProgress)
		const rz = rotateFromZ * (1 - easedProgress)
		out += 'perspective(1000px) rotateX(' + rx.toFixed(2) + 'deg) rotateY(' + ry.toFixed(2) + 'deg) rotateZ(' + rz.toFixed(2) + 'deg) '
	}

	return out.length > 0 ? out.substring(0, out.length - 1) : ''
}

export const applyEasedProgress = (el, cfg, naturalRect, eased) => {
	rememberNaturalRect(cfg, naturalRect)
	const scrollAnimTransform = buildTransform(
		naturalRect,
		eased,
		cfg.scaleEnabled,
		cfg.scaleFactor,
		cfg.directionEnabled,
		cfg.direction,
		cfg.distancePct,
		cfg.skewEnabled,
		cfg.skewFromX,
		cfg.skewFromY,
		cfg.rotateEnabled,
		cfg.rotateFromX,
		cfg.rotateFromY,
		cfg.rotateFromZ,
		cfg.scaleToElementSize,
	)
	const parallaxTransform = el._layParallaxTransform || ''
	const baseTransform = cfg.baseTransform || ''
	const transformParts = []
	if (baseTransform) transformParts.push(baseTransform)
	if (parallaxTransform) transformParts.push(parallaxTransform)
	if (scrollAnimTransform) transformParts.push(scrollAnimTransform)
	const transform = transformParts.join(' ')
	if (cfg._lastTransform !== transform) {
		cfg._lastTransform = transform
		el.style.transform = transform
	}

	applyOpacity(el, eased, cfg.fadeEnabled, cfg.fadeFrom, cfg)
	applyFilter(el, eased, cfg.blurEnabled, cfg.blurFromPx, cfg.grayscaleEnabled, cfg.grayscaleFromPct, cfg.brightnessEnabled, cfg.brightnessFromPct, cfg)
	applyClipPath(el, eased, cfg.revealEnabled, cfg.revealDirection, cfg)
}

export const resetElementStyleCache = (cfg) => {
	cfg._lastTransform = undefined
	cfg._lastOpacity = undefined
	cfg._lastFilter = undefined
	cfg._lastClipPath = undefined
}
